import { ANNOTATION_TARGETS } from 'constants';
import { EXPANDABLE_PLAYER_ANNOTATIONS } from 'constants/annotations'
import * as is from 'utilities/is';
import merge from 'deepmerge';
import RecursiveIterator from 'recursive-iterator';
import { orderBy, collect, unique } from 'utilities/list';
import { supportsDateInput } from 'utilities/context';
import { pick, omit, aliasProperties } from 'utilities/data';
import { captionToBookmark, templatizableProperties } from 'utilities/annotations';
import {
    ANNOTATION_SCHEMA,
    LEGACY_PROPERTY_MAP,
    OVERRIDE_CONFIG,
    ANIMATION_SETTINGS
} from 'configuration/defaults';
import { forceTranscriptToEndOfAnnotationList } from './migrate/smart_captions';
import { removeQuizProgress } from './migrate/remove_quiz_progress';
import { removeBackButton } from './migrate/remove_back_button';
import { removeTranscriptAnnotation } from './migrate/remove_transcript_annotation';
import { checkQuizActionsAndAddPhantomAnnotation } from './migrate/check_quiz_actions_for_start_time';
import { removeFinalMessages } from './migrate/remove_final_messages';
import { removePanelChangeFromQuiz } from './migrate/remove_panel_change_from_quiz'

export const MAX_MESSAGE_LIST_SIZE = 10;
export const SAFEGUARD_MESSAGE_PROPS = ['annotations'];

// these properties do not require relative-to-absolute parsing
export const RELATIVE_PARSE_EXCLUSIONS = [
    'animation'
];

export const parseLegacyBookmarks = comments => {
    return (
        comments
            .filter(c => c.type === 'bookmark')
            .map(b => {
                return {
                    id: b.id,
                    content: b.properties.popcorn.text,
                    start: b.properties.popcorn.start
                }
            })
            .sort((a, b) => b.content > a.content ? -1 : 1)
    );
};

export const parseLegacySetup = data => {
    const defaults = data.configuration.player.defaults;
    let parsed;

    return {
        media: [{
            url: data.video.properties.sources[0].url,
            mime: data.video.properties.sources[0].mime_type,
            duration: data.video.duration,
            width: data.project.width,
            height: data.project.height
        }],
        annotations: data.comments.map(comment => {
            parsed = {
                target: ANNOTATION_TARGETS.PLAYER,
                type: comment.type,
                id: comment.id,
                customConfig: comment.custom_configuration,
                ...legacyOptionsToAnnotationSchema(comment.properties.popcorn)
            };

            parsed = {
                ...parsed,
                properties: {
                    ...parsed.properties,
                    ...addDefaults(defaults, comment.type)
                }
            }

            if (comment.type === 'contents') {
                parsed.bookmarks = parseLegacyBookmarks(data.comments);
            }

            return parsed;
        }),
        project: {
            tags: data.project.tags,
            ...data.project
        },
        legacyConfig: {
            ...data
        },
        analytics: {
            ...data.configuration.analytics
        },
        group: {
            ...data.configuration.group
        },
        video: {
            ...data.video
        },
        track: {
            ...data.track
        }
    };
};

export const parseOverrides = (overrides) => {
    const { annotations } = overrides;

    distillAndApplyMetadata(annotations);

    // this should eventually be moved into a default config migration, but leaving for now to reduce testing scope
    overrides.variables = overrides.variables || {};
    overrides.analytics = {
        ...OVERRIDE_CONFIG.analytics,
        ...overrides.analytics,
    }

    return overrides;
};

export const applicableBookmark = id => bookmark => (
    bookmark.appliesTo ?
        [].concat(bookmark.appliesTo).includes(id) :
        true // assume if bookmark does not include appliesTo, it should be provided to all annotations
);

export const legacyOptionsToAnnotationSchema = popcornValues => {
    popcornValues = { 
        ...popcornValues,
        ...aliasLegacyProperties(popcornValues, LEGACY_PROPERTY_MAP)
    };

    const topLevel = pick(popcornValues, ANNOTATION_SCHEMA.TOP_LEVEL_PROPERTIES),
        properties = omit(popcornValues, ANNOTATION_SCHEMA.TOP_LEVEL_PROPERTIES);

    return {
        ...topLevel,
        properties
    }
};

export const aliasLegacyProperties = (values, map) => {
    return Object.keys(map).reduce((mapped, key) => {
        if (values[key]) {
            mapped[map[key]] = values[key];
        }

        return mapped;
    }, {});
};

export const addDefaults = (defaults, type) => defaults[type] || {};

export const safeJSON = src => {
    try {
        if (typeof src === 'object') {
            return src;
        } else if (typeof src === 'string') {
            return JSON.parse(src);
        }
    } catch(e) {
        console.warn('error parsing JSON', src, e);
    }

    return {};
};

export const preventCircular = found => {
    return (k,v) => {
        if (typeof v === 'object' && v !== null) {
            if (found.includes(v)) {
                return;
            }
            found.push(v);
        }
        return v;
    };
}

export const safeStringify = data => {
    try {
        if (is.object(data)) {
            return JSON.stringify(data);
        } else if (is.string(data)) {
            return data;
        }
    } catch(e) {
        console.warn('error stringifying JSON', data, e);
    }

    return '';
}

export const safeSerialize = data => {
    const potentialRisk = data && (data.args || data.overrides); // either item may have large lists of items 

    if (potentialRisk && typeof potentialRisk === 'object') {
        Object.keys(potentialRisk).forEach(key => {
            // prevent props with long lists from sending the entire list
            if (SAFEGUARD_MESSAGE_PROPS.includes(key) && potentialRisk[key].length && potentialRisk[key].length > MAX_MESSAGE_LIST_SIZE) {
                potentialRisk[key] = potentialRisk[key].slice(0, MAX_MESSAGE_LIST_SIZE);
            }
        })
    }

    return safeStringify(data);
}

export const eventHandlers = options => Object.keys(options).filter(k => k.indexOf('on') === 0 && is.fn(options[k]));

// proxy `onEventName` properties with all lowercase versions to match documentation
export const normalizeOptions = (options = {}) => {
    const events = eventHandlers(options).reduce((handlers, e) => ({ ...handlers, [e.toLowerCase()]: options[e] }), {});

    if (options.thumbnail && options.overrides?.project) {
        options.overrides.project.thumbnail = options.thumbnail;
    }
    
    return {
        ...options,
        ...events
    }
};

// migrate older configs to current
// only a few transformations for now
// intention is to add more as configuration changes
export const migrate = (config = {}) => (
    [
        useProjectDisplay,
        applyAdjacentSizes,
        applyDefaultConfig,
        // applyDefaultAnimation,
        convertVariationShorthand, // must be converted before languages are collected
        collectLanguages,
        collectLevels,
        updateCaptionsSource,
        convertHiddenAnnotations,
        convertVariationShorthand,
        makeFormAnnotationsSafe,
        addExpandableBehaviors,
        localizedToVideoText,
        addTimeStamp,
        applyContext,
        checkQuizActionsAndAddPhantomAnnotation,
        removeQuizProgress,
        removeBackButton,
        removeFinalMessages,
        removePanelChangeFromQuiz,
        removeTranscriptAnnotation,
        forceTranscriptToEndOfAnnotationList, //must always be the last migration for accessibility as long as player renders transcript.
    ]
        .reduce((merged, fn) => ({ ...merged, ...fn(merged) }), config)
);

export const useProjectDisplay = config => {
    if (config.project && config.project.display && !config.display) {
        config.display = { ...config.project.display };
    }

    return config;
};

// Use adjacent size config as width/height for surround layout
// Assuming config is from before layouts were configurable
// So apply adjacent sizes to default surround layout
export const applyAdjacentSizes = config => {
    if (config.display && config.display.adjacent && !config.display.layout) {
        const { percentageVertical, percentageHorizontal } = config.display.adjacent;

        config.display = {
            ...config.display,
            layout: [
                ...OVERRIDE_CONFIG.display.layout
                    .map(l => {
                        if (is.defined(percentageVertical) && (l.name === ANNOTATION_TARGETS.ADJACENT_TOP || l.name === ANNOTATION_TARGETS.ADJACENT_BOTTOM)) {
                            l = { ...l, height: percentageVertical };
                        }

                        if (is.defined(percentageHorizontal) && (l.name === ANNOTATION_TARGETS.ADJACENT_LEFT || l.name === ANNOTATION_TARGETS.ADJACENT_RIGHT)) {
                            l = { ...l, width: percentageHorizontal };
                        }

                        return l;
                    })
            ]
        };
    }

    return config;
};

export const applyDefaultConfig = config => {
    const applied = {};

    Object.keys(OVERRIDE_CONFIG).forEach(key => {
        if (key === 'annotations') {
            // annotations can be very large, so we want to avoid merge operations on it
            // so just put an empty array in if it's not included
            applied[key] = config[key] || [];
            return;
        }

        if (!config[key]) {
            applied[key] = { ...OVERRIDE_CONFIG[key] };
        } else if (key === 'display') {
            applied[key] = merge(OVERRIDE_CONFIG.display, config.display);

            // `deepmerge` will combine arrays, not replace
            // check if layout already exists to prevent duplicates
            if (config.display.layout) {
                applied[key] = {
                    ...applied[key],
                    layout: [...config.display.layout]
                }
            }
        } else {
            applied[key] = merge(OVERRIDE_CONFIG[key], config[key]);
        }
    });

    return applied;
};

export const collectStateValues = (stateName = '', list = []) => (
    list
        .filter(({ variations = [] }) => (
            variations.some(({ conditions = [] }) => (
                conditions.some(c => c.state === stateName)
            ))
        ))
        .flatMap(({ variations = [] }) => (
            variations
                .filter(v => v?.conditions?.filter(c => c.state === stateName))
        ))
        .map(i => i.conditions.filter(c => c.state === stateName))
        .flatMap(v => v.map(c => c.value))
        .filter(unique)
)

export const collectLanguages = config => {
    const { annotations = [], project = {} } = config;
    const languages = collectStateValues('language', annotations);

    return {
        ...config,
        project: {
            ...project,
            languages
        }
    }
};

export const collectLevels = config => {
    const { annotations = [], project = {} } = config;
    const levels = collectStateValues('level', annotations);

    return {
        ...config,
        project: {
            ...project,
            levels
        }
    }
}

// performs in object replace of relative asset paths with absolute paths based on base path
export const relativeToAbsolute = (basePath, subject) => {
    const iterator = new RecursiveIterator(subject);
    iterator.onStepInto = ({key}) => !RELATIVE_PARSE_EXCLUSIONS.includes(key);

    for (let { key, node, parent } of iterator) {
        let replacement;

        if (is.string(node)) {
            if (node.startsWith('../')) {
                replacement = node.replace('../', basePath);
            }

            if (node.startsWith('./')) {
                replacement = node.replace('./', basePath);
            }

            parent[key] = replacement || node;
        }
    }
    
    return subject;
} 

export const applyDefaultAnimation = config => {
    config.annotations = (config.annotations || []).map(annotation => ({
        ...annotation,
        animation: merge(ANIMATION_SETTINGS, (annotation.animation || {}))
    }));

    return config;
};


export const updateCaptionsSource = config => {
    config.annotations = (config.annotations || []).map(a => {
        if (a.type === 'closedcaptions') {
            a = upgradeCaption(a);
        }

        return a
    })

    return config;
}

export const upgradeCaption = caption => {
    caption.properties = caption.properties || {};

    caption.content = caption.content || caption.properties.src || '';
    caption.contentType = caption.contentType || caption.properties.format || (caption.content.startsWith('WEBVTT') ? 'string' : 'url'); // guess format if not specified

    delete caption.properties.src;
    delete caption.properties.format;

    if (caption.variations && caption.variations.length) {
        caption.variations = caption.variations.map(v => {
            return {
                ...v,
                changes: upgradeCaption(v.changes)
            }
        });
    }

    return caption;
}

export const distillAndApplyMetadata = (annotations = []) => {
    const applicables = annotations.filter(a => a.appliesTo);
    const metadata = {
            captions: annotations.filter(a => a.type === 'closedcaptions').map(captionToBookmark).reduce((r, c) => r.concat(c), []),
            bookmarks: annotations.filter(a => a.type === 'bookmark').map(collect('start', 'end', 'id', 'thumbnail', 'content', 'appliesTo')).sort(orderBy('start')),
            annotations: annotations.filter(a => a.type !== 'bookmark' && a.type !== 'closedcaptions').map(collect('id', 'type', 'start', 'content', 'properties')).sort(orderBy('start')),
            powerWords: annotations.filter(a => a.type === "powerwords").map(w => w.content.split(',').map(a => a.toLowerCase().trim()))[0]
    }
    const startTimes = annotations.reduce((times, a) => {
        times[a.id] = a.start;
        return times;
    }, {});

    for (let i = 0, len = annotations.length; i < len; i++) {
        const { id } = annotations[i];

        annotations[i].metadata = metadata;
        annotations[i].startTimes = startTimes;
        annotations[i].bookmarks = metadata.bookmarks.filter(applicableBookmark(id));
        annotations[i].descendants = applicables.filter(a => is.array(a.appliesTo) ? a.appliesTo.includes(id) : a.appliesTo === id);
        annotations[i].actions = (annotations[i].actions || []).map(a => a.event ? a : { ...a, event: 'onClick' }); // "onClick" is default event if not specified,
        annotations[i].powerWords = metadata.powerWords
    }

    return annotations;
}

export const convertHiddenAnnotations = config => {
    config.annotations = (config.annotations || []).map(annotation => {
        if (annotation.target === 'hidden') {
            annotation.target = 'player'

            if (!annotation.styles) {
                annotation.styles = {
                    display: 'none'
                }
            } else if (annotation.styles["compositionTypes.root"] && typeof annotation.styles["compositionTypes.root"] === 'object') {
                annotation.styles["compositionTypes.root"].display = 'none'
            } else if (!annotation.styles["compositionTypes.root"]) {
                annotation.styles.display = 'none'
            }
        }
        return annotation
    })
    return config
}


// convert variation shorthand to the standard syntax
export const convertVariationShorthand = config => {
    const { defaults = {} } = config,
        { annotations = {} } = defaults;

    const variationDefaults = annotations.variations;

    config.annotations = (config.annotations || []).map(annotation => {
        if (annotation.variations) {
            annotation.variations = annotation.variations.map(variation => {
                if (!variation.conditions && !variation.changes && variationDefaults) {
                    const { state, change } = variationDefaults;

                    if (state && change) {
                        const [value] = Object.keys(variation);

                        variation = {
                            conditions: [
                                { state, value, assertion: 'equal' }
                            ],
                            changes: {
                                [change]: variation[value] 
                            }
                        }
                    }
                }

                return variation;
            })
        }

        return annotation;
    })

    return config;
}

export const makeFormAnnotationsSafe = (config = {}) => {
    config.annotations = (config.annotations || []).map(a => {
        if (a.type === 'form') {
            a.content = (
                (a.content || '')
                    .replace('type="textarea"', '') // strip invalid type attr on text areas
                    .replace(/(<\s*form[^>]*>|<\s*\/\s*form>)/gi,'') // remove form element, component handles that
            );

            if (!supportsDateInput) {
                // if we don't remove date type attribute from input elements, it will break parsing on IE
                a.content = a.content.replace(/type="date"/gi, '');
            }
        }

        return a
    })

    return config;
}

export const addExpandableBehaviors = (config = {}) => {
    // this migration is used for including the 'behaviors' property on the annotaitons object while its not part of the tools app schema.
    // won't be needed once the that work has been done. 
    const checkBehaviors = (b) => {
        if (b && b.behaviors && b.behaviors.includes('expandable')) {
            return true; 
        } 
        return false;
    }

    config.annotations = (config.annotations || []).map(a => {
        const { toolType, target, properties} = a;
        const behaviorExists = checkBehaviors(a.behaviors ? a.behaviors : null);

        if (EXPANDABLE_PLAYER_ANNOTATIONS.includes(toolType) && target === 'player' && (properties && properties.showExpanded)) {
            if (behaviorExists) { 
                return; 
            } else if (!behaviorExists && !a.behaviors) { 
                a.behaviors = [];
            }
            
            a.behaviors.push("expandable");
        }
        return a;
    });

    return config;
}

const TEMPLATE_MATCHER = /\{\{\s*([A-z0-9\s.\-]+)\s*\}\}/g;

// takes templatized string ('blah {{some.foo}}, {{some.bar}}') and returns list of found variables
export const applyVariables = (template = '', scope = {}) => {
    const replacer = RegExp(TEMPLATE_MATCHER.source);

    let matched = TEMPLATE_MATCHER.exec(template),
        applied = template;

    while(matched) {
        const [,match] = matched;
        let value = (match||'').trim().split('.').reduce((s,m) => s && s[m], scope);

        if (!is.defined(value)) {
            value = '';
        }

        applied = applied.replace(replacer, value);

        matched = TEMPLATE_MATCHER.exec(template);
    }

    return applied;
}

export const collectTemplateStates = (subject = {}, schema = 'annotations') => {
    let collected = [];
    let templatizeable;

    // todo - add support for template checks in "layouts" and other config properties
    if (schema === 'annotations') {
        // leverage knowledge of annotation schema to optimize template identification
        templatizeable = templatizableProperties;
    }

    if (templatizeable) {
        collected = (
            templatizeable
                .map(t => subject[t])
                .reduce(collectStates, collected)
        )
    }

    return collected;
}

export const localizedToVideoText = (config = {}) => {
    if (config.annotations) {
        const videoTextAnnotations = config.annotations.filter(({properties={}}) => properties.localized);
        
        if (!videoTextAnnotations.length) {
            return config;
        }

        config.annotations = config.annotations.filter(({properties={}}) => !properties.localized);
        const { animation = {} } = videoTextAnnotations[0];
        
        const videoText = {
            id: 'migrated_videotext',
            type: 'videotext',
            content: 'content not used',
            start: 0,
            target: 'player',
            styles: {},
            nestedContent: true,
            animation: {
                props: [],
                frames: {},
                resolution: animation.resolution,
                interpolate: animation.interpolate
            }
        };

        const compositeFields = ['id','start','end','content','styles','animation'];
        const videoTextProperties = compositeFields.reduce((fields,f) => ({...fields,[f]:[]}), {});

        videoText.properties = videoTextAnnotations.reduce((properties,a) => {
            compositeFields.forEach(field => properties[field].push(a[field]));
            return properties;
        }, videoTextProperties);

        videoText.properties.languageVisibility = (
            config
                .annotations
                .filter(a => a.type === 'languageprovider' || a.type === 'conditionalsettingsprovider')
                .flatMap(a => a.variations)
                .map(v => {
                    const language = v.conditions.find(c => c.state === 'language').value;
                    const location = v.changes.content;
                    return { [language]: location !== null }
                })
                .reduce((visibility, lang) => ({...visibility, ...lang}), {})
        );        

        config.annotations.push(videoText);
    }

    return config;
}

export const addTimeStamp = (config = {}) => {
    if (config.annotations) {
        config.annotations = config.annotations.map(a => ({
            lastModified: new Date().getTime(),
            ...a
        }))
    }

    return config;
}

export const applyContext = (config = {}) => {
    const quizIndices = config?.annotations?.reduce((quizIndices, a, i) => (
        a.type === 'quiz' ? quizIndices.concat(i) : quizIndices
    ), []);
    
    if (config?.context?.user?.permissions && quizIndices.length) {
        const { showCorrectAnswers, quizReadOnly } = config.context.user.permissions;
        
        quizIndices.forEach(quizIndex => {
            const quiz = config.annotations[quizIndex];
            quiz.properties = quiz.properties || {};
            
            if (is.defined(showCorrectAnswers)) {
                quiz.properties.showCorrect = showCorrectAnswers;
            }

            if (is.defined(quizReadOnly)) {
                quiz.properties.readOnly = quizReadOnly;
            }
        })
    }

    if (config?.context?.content?.cid) {
        config.contentId = config.context.content.cid;
    }

    return config;
}

function collectStates(collected, property) {
    if (is.string(property)) {
        collected = collected.concat(findTemplates(property));
    } else if (is.array(property)) {
        return property.reduce(collectStates, collected);
    } else if (is.object(property)) {
        return Object.entries(property).reduce(collectStates, collected);
    }

    return collected;
}

function findTemplates(str = '') {
    const found = [];
    let matched = TEMPLATE_MATCHER.exec(str);

    while (matched) {
        found.push(matched[1].trim());
        matched = TEMPLATE_MATCHER.exec(str);
    }

    return found;
}

export const removePunctuation = (word) => {
    return word.replace(/[^\w\s]|_/g, "").replace(/\s+/g, " ")
} 